// app/layout.tsx
'use client';

import { useEffect, useRef, Suspense } from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { Toaster } from 'sonner';

import { SessionProvider } from '@/providers/SessionProvider';
import { UserProvider } from '@/context/UserContext'; 
import { getToasterConfig } from '@/lib/notification/toast-manager';
 
import { CookieConsent } from './cookie-consent';
import { MainNavigation } from './navbar';
import { Footer } from './footer';
import { CartProvider } from '../cart/CartProvider';
import { VendorProvider } from '@/context/VendorContext';

/* ============================================================
   | TYPES
   ============================================================ */

interface LayoutProvidersProps {
  children: React.ReactNode; 
  maintenanceMode?: boolean; 
  initialUser?: any;
}

interface RouteConfig {
  path: string;
  hideLayout?: boolean;
  requiresAuth?: boolean;
}

/* ============================================================
   | ROUTE CONFIGURATION
   ============================================================ */

const ROUTE_CONFIGS: readonly RouteConfig[] = [
  { path: '/login', hideLayout: true },
  { path: '/signup', hideLayout: true },
  { path: '/dashboards', requiresAuth: true },
  { path: '/checkout', requiresAuth: true },
  { path: '/settings', requiresAuth: true },
] as const;

function resolveRouteConfig(pathname: string | null): RouteConfig {
  if (!pathname) return { path: '' };

  const matchedConfig = ROUTE_CONFIGS.find(
    (config) =>
      pathname === config.path ||
      pathname.startsWith(`${config.path}/`)
  );
  
  return matchedConfig ?? { path: pathname };
}

/* ============================================================
   | SONNER TOASTER COMPONENT (Fixed TypeScript)
   ============================================================ */

function AppToaster() {
  return (
    <Toaster
      position="top-center"
      richColors
      closeButton
      expand={false}
      duration={5000}
      visibleToasts={3}
      gap={12}
      offset={20}
      toastOptions={getToasterConfig()}
    />
  );
}

/* ============================================================
   | QUERY PARAM NOTIFICATION HANDLER (Optimized)
   ============================================================ */

function QueryParamHandler() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const processedRef = useRef<string>('');

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const paramString = params.toString();
    
    // Skip if already processed these exact params
    if (processedRef.current === paramString) return;
    
    let shouldReplace = false;
    const { notify } = require('@/lib/notification/toast-manager');

    // Handle all notification types
    const notificationTypes = [
      { key: 'success', method: notify.success },
      { key: 'error', method: notify.error },
      { key: 'warning', method: notify.warning },
      { key: 'info', method: notify.info },
    ];

    notificationTypes.forEach(({ key, method }) => {
      const value = params.get(key);
      if (value) {
        try {
          method(decodeURIComponent(value));
          params.delete(key);
          shouldReplace = true;
        } catch (error) {
          console.error(`Failed to process ${key} param:`, error);
        }
      }
    });

    if (shouldReplace) {
      const newUrl = params.toString() 
        ? `${pathname}?${params.toString()}` 
        : pathname;
      
      router.replace(newUrl, { scroll: false });
      processedRef.current = paramString;
    }
  }, [searchParams, pathname, router]);

  return null;
}

/* ============================================================
   | PERFORMANCE MONITORING
   ============================================================ */

function PerformanceMonitor() {
  const pathname = usePathname();
  const routeStartTime = useRef<number>(0);

  useEffect(() => {
    // Measure route transition performance
    routeStartTime.current = performance.now();
    
    const handleLoad = () => {
      const loadTime = performance.now() - routeStartTime.current;
      
      // Log performance data (could send to analytics)
      if (loadTime > 1000) {
        console.warn(`Slow route load: ${pathname} took ${loadTime.toFixed(0)}ms`);
      }
      
      // Dispatch custom event for analytics
      window.dispatchEvent(new CustomEvent('route:loaded', {
        detail: { path: pathname, loadTime }
      }));
    };

    // Wait for next tick to ensure DOM is ready
    setTimeout(handleLoad, 0);

    return () => {
      routeStartTime.current = 0;
    };
  }, [pathname]);

  return null;
}

/* ============================================================
   | MAIN LAYOUT PROVIDER
   ============================================================ */

  export default function LayoutProviders({
    children, 
    maintenanceMode = false,
    initialUser = null,
  }: LayoutProvidersProps) {
    const pathname = usePathname();
    const routeConfig = resolveRouteConfig(pathname);
    const isPublicPage = pathname === '/' || pathname?.startsWith('/vendors') || pathname?.startsWith('/pages/'); 
  
     
    useEffect(() => {
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Set focus to main content for accessibility
      const mainElement = document.querySelector('main');
      if (mainElement && !routeConfig.hideLayout) {
        mainElement.setAttribute('tabindex', '-1');
        mainElement.focus();
      }
    }, [pathname, routeConfig.hideLayout]);
  
    /* ============================================================
       | MAINTENANCE MODE HANDLING
       ============================================================ */
  
    if (maintenanceMode && !pathname?.startsWith('/admin')) {
      return (<SessionProvider skipInitialCheck={true}> {/* Skip initial check to avoid race */}
        <UserProvider initialUser={initialUser}>
            <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100">
              <div className="text-center max-w-md">
                <div className="mb-6">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    Maintenance Mode
                  </h1>
                  <p className="text-gray-600 mb-6">
                    We're performing scheduled maintenance to improve your experience. 
                    Please check back in 30 minutes.
                  </p>
                  <div className="text-sm text-gray-500">
                    <p>Estimated completion: 30 minutes</p>
                    <p className="mt-1">Status: System updates in progress</p>
                  </div>
                </div>
              </div>
            </div>
            <AppToaster />
          </UserProvider>
        </SessionProvider>
      );
    }
  
    /* ============================================================
       | NO-LAYOUT ROUTES (AUTH PAGES)
       ============================================================ */
  
    if (routeConfig.hideLayout) {
      return (<SessionProvider skipInitialCheck={true}> {/* Skip initial check to avoid race */}
        <UserProvider initialUser={initialUser}>
            <Suspense fallback={null}>
              <QueryParamHandler />
            </Suspense>
            <PerformanceMonitor />
                    <VendorProvider>
                      <CartProvider>
            <main className="min-h-screen bg-white">
              {children}
            </main>
                      </CartProvider>
                    </VendorProvider>
            <AppToaster />
          </UserProvider>
        </SessionProvider>
      );
    }
  
    /* ============================================================
       | FULL APPLICATION SHELL
       ============================================================ */
  
    return (<SessionProvider skipInitialCheck={true}> {/* Skip initial check to avoid race */}
        <UserProvider initialUser={initialUser}>
          <Suspense fallback={null}>
            <QueryParamHandler />
          </Suspense>
          <PerformanceMonitor />
          
          {/* Conditional Navigation */}
          {isPublicPage && <MainNavigation />}
  
                    <VendorProvider>
                      <CartProvider> 
            <main className="min-h-screen" id="main-content" tabIndex={-1}>
              {children}
            </main> 
                    </CartProvider>
                  </VendorProvider>
  
          {/* Conditional Footer */}
          {isPublicPage && <Footer />}
  
          {/* Global Components */}
          <AppToaster />
          <CookieConsent /> 
        </UserProvider>
      </SessionProvider>
    );
  }